// PictureVideoPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BrowserProvider, Contract, parseEther } from "ethers";

// Helper: returns post.id if available, otherwise post._id.
const getPostId = (post) => post.id || post._id;

// Contract configuration
const CONTRACT_ADDRESS = "0x1c713297e32c91694135d552422d6c9f914bdae6";
const CONTRACT_ABI = [
  {
    "anonymous": false,
    "inputs": [
      { "indexed": true, "internalType": "uint256", "name": "nftId", "type": "uint256" },
      { "indexed": true, "internalType": "address", "name": "minter", "type": "address" },
      { "indexed": false, "internalType": "uint256", "name": "amountPaid", "type": "uint256" }
    ],
    "name": "MintedToCastle",
    "type": "event"
  },
  {
    "inputs": [
      { "internalType": "uint256", "name": "nftId", "type": "uint256" },
      { "internalType": "uint256", "name": "price", "type": "uint256" }
    ],
    "name": "mintCastle",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }],
    "name": "mintedCount",
    "outputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }],
    "stateMutability": "view",
    "type": "function"
  }
];

// Connect to MetaMask using ethers v6 BrowserProvider.
async function connectWallet() {
  if (window.ethereum) {
    try {
      await window.ethereum.request({ method: "eth_requestAccounts" });
      const provider = new BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const userAddress = await signer.getAddress();
      console.log("Wallet connected:", userAddress);
      return { provider, signer, userAddress };
    } catch (error) {
      console.error("User denied account access", error);
      throw new Error("Wallet connection failed");
    }
  } else {
    alert("MetaMask is not installed.");
    throw new Error("No wallet provider found");
  }
}

// Instantiate the contract with the signer.
async function getContract(signer) {
  return new Contract(CONTRACT_ADDRESS, CONTRACT_ABI, signer);
}

export default function PictureVideoPage() {
  const navigate = useNavigate();
  // Use a constant tag for record comments. Adjust as needed.
  const userTag = "YourTag";
  const [posts, setPosts] = useState([]);
  const [lightedPosts, setLightedPosts] = useState({});
  const [mintedPosts, setMintedPosts] = useState({});
  const [filter, setFilter] = useState("RECENT");
  const [selectedPost, setSelectedPost] = useState(null);

  // Fetch NFT posts when the component mounts.
  useEffect(() => {
    const fetchNFTs = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_API_URL;
        if (!backendUrl) throw new Error("Backend API URL not defined.");
        const response = await fetch(`${backendUrl}/api/nfts`);
        if (!response.ok) throw new Error("Failed to fetch NFTs");
        const nftsData = await response.json();
        setPosts(nftsData);
      } catch (error) {
        console.error("Error fetching NFTs:", error);
      }
    };
    fetchNFTs();
  }, []);
  

  // Sort posts based on the selected filter.
  const filterPosts = () => {
    let filtered = [...posts];
    switch (filter) {
      case "BRiGHTEST":
        return filtered.sort((a, b) => (b.lights || 0) - (a.lights || 0));
      case "NEED LiGHT":
        return filtered.sort((a, b) => (a.lights || 0) - (b.lights || 0));
      default:
        return filtered;
    }
  };

  const toggleLight = (e, id) => {
    e.stopPropagation();
    if (!id) return;
    const idStr = id.toString();
    if (lightedPosts[idStr]) {
      setPosts((prev) =>
        prev.map((post) =>
          getPostId(post)?.toString() === idStr
            ? { ...post, lights: Math.max((post.lights || 0) - 1, 0) }
            : post
        )
      );
      setLightedPosts((prev) => {
        const newObj = { ...prev };
        delete newObj[idStr];
        return newObj;
      });
    } else {
      setPosts((prev) =>
        prev.map((post) =>
          getPostId(post)?.toString() === idStr
            ? { ...post, lights: (post.lights || 0) + 1 }
            : post
        )
      );
      setLightedPosts((prev) => ({ ...prev, [idStr]: true }));
    }
  };

  const addRecord = (e, id) => {
    e.stopPropagation();
    if (!id) return;
    const newRecord = prompt("Add your RECORD");
    if (newRecord) {
      setPosts((prev) =>
        prev.map((post) =>
          getPostId(post)?.toString() === id.toString()
            ? {
                ...post,
                records: (post.records || 0) + 1,
                comments: [
                  ...(post.comments || []),
                  { text: newRecord, tag: userTag, lights: 0 },
                ],
              }
            : post
        )
      );
    }
  };

  const addToCastle = async (e, id) => {
    e.stopPropagation();
    if (!id) return;
    const idStr = id.toString();
    if (mintedPosts[idStr]) {
      alert("You have already minted this NFT to your castle.");
      return;
    }
    const targetPost = posts.find(
      (post) => getPostId(post)?.toString() === idStr
    );
    if (!targetPost) return;
    const originalSupply = Number(targetPost.supply) || 0;
    const minted = Number(targetPost.castleAdds) || 0;
    const remaining = originalSupply - minted;
    if (remaining <= 0) {
      alert("This NFT is sold out.");
      return;
    }
    const confirmMessage =
      `Price: ${targetPost.price || "N/A"} ETH\n` +
      `Original Supply: ${originalSupply}\n` +
      `Remaining: ${remaining}\n\n` +
      `Your wallet will now be prompted to confirm the minting transaction.`;
    if (!window.confirm(confirmMessage)) return;
    try {
      const { signer } = await connectWallet();
      const contract = await getContract(signer);
      const tx = await contract.mintCastle(
        targetPost.id,
        parseEther(targetPost.price.toString()),
        { value: parseEther(targetPost.price.toString()) }
      );
      await tx.wait();
      setPosts((prev) =>
        prev.map((post) =>
          getPostId(post)?.toString() === idStr
            ? { ...post, castleAdds: (post.castleAdds || 0) + 1, inCrypt: true }
            : post
        )
      );
      setMintedPosts((prev) => ({ ...prev, [idStr]: true }));
      await fetch(`/api/nfts/${targetPost.id}/mint`, { method: "PUT" });
      alert("Wallet transaction confirmed. NFT minted to your castle.");
    } catch (error) {
      console.error("Minting failed:", error);
      alert("Minting failed. Please try again.");
    }
  };

  const deletePost = (e, id) => {
    e.stopPropagation();
    if (!id) return;
    if (!window.confirm("Are you sure you want to delete this post?"))
      return;
    setPosts((prev) =>
      prev.filter((post) => getPostId(post)?.toString() !== id.toString())
    );
    setLightedPosts((prev) => {
      const newObj = { ...prev };
      delete newObj[id.toString()];
      return newObj;
    });
    setMintedPosts((prev) => {
      const newObj = { ...prev };
      delete newObj[id.toString()];
      return newObj;
    });
  };

  const toggleVisibility = (e, id) => {
    e.stopPropagation();
    if (!id) return;
    setPosts((prev) =>
      prev.map((post) => {
        if (getPostId(post)?.toString() === id.toString()) {
          const newVisibility =
            post.visibility === "private" ? "public" : "private";
          return { ...post, visibility: newVisibility };
        }
        return post;
      })
    );
  };

  const closeModal = () => setSelectedPost(null);

  return (
    <div className="min-h-screen bg-brandBlack text-brandWhite p-6">
      {/* Customizable Description */}
      <div className="mb-6 text-center">
        <h2 className="text-3xl font-bold">GALLERY</h2>
        <p className="text-lg">
        
        </p>
      </div>

      {/* Filter Buttons */}
      <div className="text-center mb-4">
        <button
          onClick={() => setFilter("RECENT")}
          className={`p-2 mx-2 ${filter === "RECENT" ? "bg-gray-700 text-white" : "bg-gray-400"}`}
        >
          MOST RECENT
        </button>
        <button
          onClick={() => setFilter("BRiGHTEST")}
          className={`p-2 mx-2 ${filter === "BRiGHTEST" ? "bg-gray-700 text-white" : "bg-gray-400"}`}
        >
          BRiGHTEST
        </button>
        <button
          onClick={() => setFilter("NEED LiGHT")}
          className={`p-2 mx-2 ${filter === "NEED LiGHT" ? "bg-gray-700 text-white" : "bg-gray-400"}`}
        >
          NEED LiGHT
        </button>
      </div>

      {/* Main Feed – Only Picture/Video Posts */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filterPosts()
          .filter((post) => post.type === "pictureVideo")
          .map((post) => {
            const postId = getPostId(post);
            const originalSupply = Number(post.supply) || 0;
            const minted = Number(post.castleAdds) || 0;
            const remaining = originalSupply - minted;
            return (
              <div
                key={postId}
                onClick={() => setSelectedPost(post)}
                className="border border-gray-700 rounded overflow-hidden p-4 cursor-pointer flex flex-col justify-between"
              >
                {/* Media Container */}
                <div className="h-40">
                  {post.video ? (
                    <video
                      controls
                      className="w-full h-full object-cover"
                      poster={post.image}
                    >
                      <source src={post.video} type="video/mp4" />
                    </video>
                  ) : (
                    <img
                      src={post.image}
                      alt={post.title || "Picture"}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>

                {post.title && (
                  <h2 className="text-lg font-gothic mt-2 text-center">
                    {post.title}
                  </h2>
                )}

                <p className="text-center text-sm mt-1">
                  Price: {post.price || "N/A"} | Supply: {post.supply || "N/A"}
                </p>

                <div className="flex justify-around mt-4">
                  <button onClick={(e) => toggleLight(e, postId)}>
                    ☉ LiGHT {post.lights || 0}
                  </button>
                  <button onClick={(e) => addRecord(e, postId)}>
                    + RECORD {post.records || 0}
                  </button>
                  <button onClick={(e) => addToCastle(e, postId)}>
                    ⛫ REMAINING: {remaining}
                  </button>
                </div>

                <div className="flex justify-around mt-2">
                  <button
                    onClick={(e) => deletePost(e, postId)}
                    className="text-red-400 underline"
                  >
                    Delete
                  </button>
                  <button
                    onClick={(e) => toggleVisibility(e, postId)}
                    className="text-blue-400 underline"
                  >
                    {post.visibility === "private" ? "PUBLIC" : "MY EYES ONLY"}
                  </button>
                </div>
              </div>
            );
          })}
      </div>

      {/* Detail View Modal */}
      {selectedPost && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center">
          <div className="bg-brandBlack p-6 rounded-lg w-full max-w-md">
            {selectedPost.type === "pictureVideo" && (
              <>
                {selectedPost.video ? (
                  <video
                    controls
                    className="w-full mb-4 object-cover"
                    poster={selectedPost.image}
                  >
                    <source src={selectedPost.video} type="video/mp4" />
                  </video>
                ) : (
                  <img
                    src={selectedPost.image}
                    alt={selectedPost.title || "Picture"}
                    className="w-full mb-4 object-cover"
                  />
                )}
              </>
            )}
            <h2 className="text-xl font-gothic mb-2">
              {selectedPost.title}
            </h2>
            <p className="text-gray-300 mb-4">
              {selectedPost.description}
            </p>
            <div className="flex justify-around mb-4">
              <button onClick={(e) => toggleLight(e, getPostId(selectedPost))}>
                ☉ {selectedPost.lights || 0}
              </button>
              <button onClick={(e) => addRecord(e, getPostId(selectedPost))}>
                + {selectedPost.records || 0}
              </button>
              {(() => {
                const originalSupply = Number(selectedPost.supply) || 0;
                const minted = Number(selectedPost.castleAdds) || 0;
                const remaining = originalSupply - minted;
                return (
                  <button onClick={(e) => addToCastle(e, getPostId(selectedPost))}>
                    ⛫ REMAINING: {remaining}
                  </button>
                );
              })()}
            </div>
            <div className="flex justify-around mb-4">
              <button
                onClick={(e) => deletePost(e, getPostId(selectedPost))}
                className="text-red-400 underline"
              >
                Delete
              </button>
              <button
                onClick={(e) => toggleVisibility(e, getPostId(selectedPost))}
                className="text-blue-400 underline"
              >
                {selectedPost.visibility === "private"
                  ? "PUBLIC"
                  : "MY EYES ONLY"}
              </button>
            </div>
            {selectedPost.comments && selectedPost.comments.length > 0 && (
              <div className="mt-4">
                <h3 className="text-lg font-gothic mb-2">Records</h3>
                <ul className="list-none">
                  {selectedPost.comments.map((comment, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <button
                        className="text-crypticGlow underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/castle/${comment.tag}`);
                        }}
                      >
                        {comment.tag}
                      </button>
                      <span className="text-gray-300">{comment.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <button
              onClick={closeModal}
              className="block mx-auto bg-gray-700 hover:bg-gray-500 text-white px-4 py-2 rounded mt-4"
            >
              CLOSE
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
